import { Link } from 'react-router-dom'
import plpService from 'Services/plpService'
import React, { useEffect, useState } from 'react'
import { HorizontalLoader, Pagination } from 'Components'
import { Logger } from 'Helpers/Logger'
import { TimeConverter } from 'Helpers/TimeConverter'
import { ShowTGAlert } from 'Helpers/ShowTGAlert'
import { CustomerType } from 'Models'
import { CurrencyFormatter } from 'Helpers'

export interface ProductLeaseType {
  id: string
  accountId: string
  principalAmountMajor: number
  totalDebtMajor: number
  currency: string
  nextLeasePaymentDueDate: string
  nextInterestRatePercentage: number
  gracePeriodDays: number
  isActive: boolean
  isFullyPaid: boolean
  autoPaymentEnabled: boolean
  lastPaymentAttemptDate: string
  lastSuccessfulPaymentDate: string
  paymentReminderSentAt: string
  orderReference: string
  createdAt: string
  updatedAt: string
  user: CustomerType
}

const ProductLease = () => {
  const [pageNumber, setPage] = useState(1)
  const [sortOrder, setSortOrder] = useState('DESC')
  const [isActive, setIsActive] = useState<boolean | undefined>(undefined)
  const [isFullyPaid, setIsFullyPaid] = useState<boolean | undefined>(undefined)
  const [autoPaymentEnabled, setAutoPaymentEnabled] = useState<boolean | undefined>(undefined)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  const [productLeases, setProductLeases] = useState<ProductLeaseType[]>([])
  const [loading, setLoading] = useState(false)

  const [paginationData, setPaginationData] = useState<{
    pageNumber: number
    pageSize: number
    total: number
  }>({
    pageNumber: 1,
    pageSize: 20,
    total: 1,
  })

  const { showAlert } = ShowTGAlert()

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const getProductLeases = async () => {
    setLoading(true)
    try {
      const response = await plpService.productLease(
        pageNumber,
        sortOrder,
        undefined,
        debouncedSearchTerm || undefined,
        undefined,
        isActive,
        isFullyPaid,
        autoPaymentEnabled,
      )
      setLoading(false)
      setProductLeases(response?.data.data.dataset || [])
      console.log('Product Leases response', response)

      setPaginationData({
        pageNumber: response?.data.data.pageNumber,
        pageSize: response?.data.data.pageSize,
        total: response?.data.data.total,
      })
    } catch (error: any) {
      setLoading(false)
      Logger('response error', error.response)
      showAlert({
        message: error.response?.data?.error || 'Error fetching product leases',
        show: true,
        title: 'Error',
        type: 'error',
      })
    }
  }

  useEffect(() => {
    getProductLeases()
  }, [pageNumber, sortOrder, isActive, isFullyPaid, autoPaymentEnabled, debouncedSearchTerm])

  const handleResetFilters = () => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setIsActive(undefined)
    setIsFullyPaid(undefined)
    setAutoPaymentEnabled(undefined)
    setSortOrder('ASC')
    setPage(1)
  }

  return (
    <>
      <div className='card border-0'>
        <div className='card-header border-0 px-0'>
          <div className='row mt-2'>
            <div className='col-12 col-md-8'>
              <div className='search-field'>
                <span>
                  <svg
                    width='18'
                    height='18'
                    viewBox='0 0 18 18'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M11.675 11.5661C9.34155 13.8995 5.55835 13.8995 3.22493 11.5661C0.891523 9.23269 0.891523 5.44948 3.22493 3.11607C5.55835 0.782656 9.34156 0.782656 11.675 3.11607C14.0084 5.44948 14.0084 9.23269 11.675 11.5661ZM11.675 11.5661L16.4126 16.3037'
                      stroke='#868484'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                    />
                  </svg>
                </span>

                <input
                  type='text'
                  className='form-control'
                  placeholder='Search by Phone Number or Order Reference'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className='col-6 col-md-2 dropdown'>
              <button
                className='btn btn-filter dropdown-toggle'
                data-bs-toggle='dropdown'
                aria-expanded='false'
              >
                <svg
                  width='17'
                  height='17'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M10.94 22.65C10.46 22.65 9.99002 22.53 9.55002 22.29C8.67002 21.8 8.14002 20.91 8.14002 19.91V14.61C8.14002 14.11 7.81002 13.36 7.50002 12.98L3.76002 9.02001C3.13002 8.39001 2.65002 7.31001 2.65002 6.50001V4.20001C2.65002 2.60001 3.86002 1.35001 5.40002 1.35001H18.6C20.12 1.35001 21.35 2.58001 21.35 4.10001V6.30001C21.35 7.35001 20.72 8.54001 20.13 9.13001L15.8 12.96C15.38 13.31 15.05 14.08 15.05 14.7V19C15.05 19.89 14.49 20.92 13.79 21.34L12.41 22.23C11.96 22.51 11.45 22.65 10.94 22.65ZM5.40002 2.85001C4.70002 2.85001 4.15002 3.44001 4.15002 4.20001V6.50001C4.15002 6.87001 4.45002 7.59001 4.83002 7.97001L8.64002 11.98C9.15002 12.61 9.65002 13.66 9.65002 14.6V19.9C9.65002 20.55 10.1 20.87 10.29 20.97C10.71 21.2 11.22 21.2 11.61 20.96L13 20.07C13.28 19.9 13.56 19.36 13.56 19V14.7C13.56 13.63 14.08 12.45 14.83 11.82L19.11 8.03001C19.45 7.69001 19.86 6.88001 19.86 6.29001V4.10001C19.86 3.41001 19.3 2.85001 18.61 2.85001H5.40002Z'
                    fill='#868484'
                  />
                  <path
                    d='M6.00004 10.75C5.86004 10.75 5.73004 10.71 5.60004 10.64C5.25004 10.42 5.14004 9.94999 5.36004 9.59999L10.29 1.69999C10.51 1.34999 10.97 1.23999 11.32 1.45999C11.67 1.67999 11.78 2.13999 11.56 2.48999L6.63004 10.39C6.49004 10.62 6.25004 10.75 6.00004 10.75Z'
                    fill='#868484'
                  />
                </svg>
                Filters
              </button>
              <div className='dropdown-menu dropdown-menu-end border-0 shadow filter-dropdown pb-auto'>
                <div className='dropdown-item'>
                  <form action=''>
                    <div className='form-group'>
                      <label htmlFor=''>Active Status</label>
                      <select
                        id=''
                        className='form-control'
                        value={isActive === undefined ? 'ALL' : isActive ? 'YES' : 'NO'}
                        onChange={(e) => {
                          setIsActive(
                            e.target.value === 'YES'
                              ? true
                              : e.target.value === 'NO'
                                ? false
                                : undefined,
                          )
                        }}
                      >
                        <option value='ALL'>All</option>
                        <option value='YES'>Active</option>
                        <option value='NO'>Inactive</option>
                      </select>
                    </div>
                    <div className='form-group mt-3'>
                      <label htmlFor=''>Fully Paid</label>
                      <select
                        id=''
                        className='form-control'
                        value={isFullyPaid === undefined ? 'ALL' : isFullyPaid ? 'YES' : 'NO'}
                        onChange={(e) => {
                          setIsFullyPaid(
                            e.target.value === 'YES'
                              ? true
                              : e.target.value === 'NO'
                                ? false
                                : undefined,
                          )
                        }}
                      >
                        <option value='ALL'>All</option>
                        <option value='YES'>Yes</option>
                        <option value='NO'>No</option>
                      </select>
                    </div>

                    <div className='form-group mt-3'>
                      <label htmlFor=''>Auto Payment</label>
                      <select
                        className='form-control'
                        value={
                          autoPaymentEnabled === undefined
                            ? 'ALL'
                            : autoPaymentEnabled
                              ? 'YES'
                              : 'NO'
                        }
                        onChange={(e) => {
                          setAutoPaymentEnabled(
                            e.target.value === 'YES'
                              ? true
                              : e.target.value === 'NO'
                                ? false
                                : undefined,
                          )
                        }}
                      >
                        <option value='ALL'>All</option>
                        <option value='YES'>Enabled</option>
                        <option value='NO'>Disabled</option>
                      </select>
                    </div>

                    <div className='form-group mt-3'>
                      <label htmlFor=''>Sort Order</label>
                      <select
                        id=''
                        className='form-control'
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                      >
                        <option>ASC</option>
                        <option>DESC</option>
                      </select>
                    </div>

                    <div className='row mt-3'>
                      <div className='col-md-6'>
                        <button
                          className='btn btn-reset'
                          type='button'
                          onClick={handleResetFilters}
                        >
                          Reset
                        </button>
                      </div>
                      <div className='col-md-6 text-end'>
                        <button className='btn btn-filter' type='button' onClick={getProductLeases}>
                          Filter
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='card-body'>
          <div className='table-responsive'>
            <table className='table'>
              <thead>
                <tr>
                  <td>ID</td>
                  <td>Phone Number</td>
                  <td>Customer Name</td>
                  <td>Order Reference</td>
                  <td>Order Amount</td>
                  <td>Remaining Debt</td>
                  <td>Status</td>
                  <td>Fully Paid</td>
                  <td>Next Payment Due</td>
                  <td>Date Created</td>
                  <td>Action</td>
                </tr>
              </thead>
              <tbody>
                {productLeases.length > 0 &&
                  productLeases.map((data: ProductLeaseType, index: number) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{data.user?.msisdn || 'N/A'}</td>
                      <td>
                        {`${data.user?.firstName || ''} ${data.user?.lastName || ''}`.trim() ||
                          'N/A'}
                      </td>
                      <td>#{data.orderReference}</td>
                      <td>
                        {data.currency}
                        {CurrencyFormatter(data.principalAmountMajor)}
                      </td>
                      <td>
                        {data.currency} {CurrencyFormatter(data.totalDebtMajor)}
                      </td>

                      <td className='text-'>
                        {data.isActive ? (
                          <span className='status fw-bolder approved'>Active</span>
                        ) : (
                          <span className='status fw-bolder rejected'>Inactive</span>
                        )}
                      </td>
                      <td>
                        {data.isFullyPaid ? (
                          <span className='status fw-bolder approved'>Yes</span>
                        ) : (
                          <span className='status fw-bolder pending'>No</span>
                        )}
                      </td>
                      <td>{TimeConverter(data.nextLeasePaymentDueDate)}</td>
                      <td>{TimeConverter(data.createdAt)}</td>
                      <td>
                        <Link to={`/product-leases/${data.id}`}>
                          <button className='btn btn-sm btn-default'>View</button>
                        </Link>
                      </td>
                    </tr>
                  ))}

                {loading === false && productLeases.length === 0 && (
                  <tr>
                    <td colSpan={11} className='text-center'>
                      No Product Lease found
                    </td>
                  </tr>
                )}
                {loading === true && productLeases.length === 0 && (
                  <tr>
                    <td colSpan={11} className='text-center'>
                      <HorizontalLoader></HorizontalLoader>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {paginationData.total >= paginationData.pageSize && (
            <div className='pagination w-100 d-flex justify-content-center'>
              {paginationData.total && (
                <Pagination
                  className='pagination-bar ps-0'
                  currentPage={Number(paginationData.pageNumber)}
                  totalCount={paginationData.total}
                  pageSize={paginationData.pageSize}
                  onPageChange={(page: React.SetStateAction<number>) => setPage(page)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default ProductLease
